import { useCallback, useEffect, useState } from "react";
import { AppShell } from "./components/AppShell";
import { GlobalSearch } from "./components/GlobalSearch";
import { QuickAddSheet } from "./components/QuickAddSheet";
import { OfflineBanner } from "./components/OfflineBanner";
import { LoginScreen } from "./components/LoginScreen";
import { DashboardView } from "./components/DashboardView";
import { MoreHub } from "./components/MoreHub";
import { FinancePage, ProjectsPage, ShoppingPage } from "./components/domains/MorePages";
import { BooksListPage, BookDetailPage } from "./components/domains/BooksPage";
import { MediaListPage, MediaDetailPage } from "./components/domains/MediaPage";
import { PlacesListPage, PlaceDetailPage } from "./components/domains/PlacesPage";
import { LoadingState } from "./components/StateViews";
import { Icon } from "./components/Icon";
import { ToastProvider, useToast } from "./components/ui/Toast";
import { checkAuth, logout } from "./lib/api";
import { navGroupFor, parseRoute } from "./lib/routes";
import { useOnlineStatus } from "./lib/useOnlineStatus";

function getCurrentPath(): string {
  return window.location.pathname;
}

type AuthStatus = "checking" | "authenticated" | "unauthenticated";

function AppInner() {
  const [path, setPath] = useState(getCurrentPath);
  const [authStatus, setAuthStatus] = useState<AuthStatus>("checking");
  const [showSearch, setShowSearch] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [offlinePreview, setOfflinePreview] = useState(false);
  const [autoOpenFinanceTransaction, setAutoOpenFinanceTransaction] = useState(false);
  const [autoOpenBookAdd, setAutoOpenBookAdd] = useState(false);
  const [autoOpenMediaAdd, setAutoOpenMediaAdd] = useState(false);
  const toast = useToast();
  const isOnline = useOnlineStatus();

  useEffect(() => {
    function handlePopState() {
      setPath(getCurrentPath());
    }
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    checkAuth(controller.signal)
      .then((authenticated) => setAuthStatus(authenticated ? "authenticated" : "unauthenticated"))
      .catch(() => {
        if (!controller.signal.aborted) setAuthStatus("unauthenticated");
      });
    return () => controller.abort();
  }, []);

  const navigate = useCallback((nextPath: string) => {
    if (!nextPath.startsWith("/")) return;
    if (nextPath === getCurrentPath()) return;
    window.history.pushState(null, "", nextPath);
    setPath(nextPath);
    window.scrollTo(0, 0);
  }, []);

  const handleLogout = useCallback(() => {
    logout()
      .catch(() => {
        // Clear local state regardless; the session cookie will simply expire.
      })
      .finally(() => setAuthStatus("unauthenticated"));
  }, []);

  const handleQuickAddSelect = useCallback(
    (label: string) => {
      toast.push(`«${label}» هنوز از طریق برنامه در دسترس نیست.`);
    },
    [toast],
  );

  const handleQuickAddFinance = useCallback(() => {
    setAutoOpenFinanceTransaction(true);
    navigate("/finance");
  }, [navigate]);

  const handleQuickAddBook = useCallback(() => {
    setAutoOpenBookAdd(true);
    navigate("/books");
  }, [navigate]);

  const handleQuickAddMedia = useCallback(() => {
    setAutoOpenMediaAdd(true);
    navigate("/media");
  }, [navigate]);

  if (authStatus === "checking") {
    return (
      <div className="auth-loading">
        <LoadingState label="در حال بررسی ورود…" />
      </div>
    );
  }

  if (authStatus === "unauthenticated") {
    return <LoginScreen onSuccess={() => setAuthStatus("authenticated")} />;
  }

  const route = parseRoute(path);
  const activeNav = navGroupFor(route);
  const openSearch = () => setShowSearch(true);

  let content;
  if (route.name === "home") {
    content = <DashboardView onNavigate={navigate} onOpenSearch={openSearch} />;
  } else if (route.name === "books") {
    content = route.id ? (
      <BookDetailPage slug={route.id} onNavigate={navigate} />
    ) : (
      <BooksListPage
        onNavigate={navigate}
        onOpenSearch={openSearch}
        autoOpenAdd={autoOpenBookAdd}
        onAutoOpenHandled={() => setAutoOpenBookAdd(false)}
      />
    );
  } else if (route.name === "media") {
    content = route.id ? (
      <MediaDetailPage slug={route.id} onNavigate={navigate} />
    ) : (
      <MediaListPage
        onNavigate={navigate}
        onOpenSearch={openSearch}
        autoOpenAdd={autoOpenMediaAdd}
        onAutoOpenHandled={() => setAutoOpenMediaAdd(false)}
      />
    );
  } else if (route.name === "places") {
    content = route.id ? (
      <PlaceDetailPage slug={route.id} onNavigate={navigate} />
    ) : (
      <PlacesListPage onNavigate={navigate} onOpenSearch={openSearch} />
    );
  } else if (route.name === "finance") {
    content = (
      <FinancePage
        onNavigate={navigate}
        autoOpenTransaction={autoOpenFinanceTransaction}
        onAutoOpenHandled={() => setAutoOpenFinanceTransaction(false)}
      />
    );
  } else if (route.name === "projects") {
    content = <ProjectsPage onNavigate={navigate} />;
  } else if (route.name === "shopping") {
    content = <ShoppingPage onNavigate={navigate} />;
  } else {
    content = (
      <MoreHub
        onNavigate={navigate}
        onLogout={handleLogout}
        offlinePreview={offlinePreview}
        onToggleOfflinePreview={() => setOfflinePreview((v) => !v)}
      />
    );
  }

  const isDetailRoute =
    (route.name === "books" || route.name === "media" || route.name === "places") && Boolean(route.id);
  const offline = offlinePreview || !isOnline;

  return (
    <>
      <AppShell
        active={activeNav}
        onNavigate={navigate}
        offlineBanner={offline ? <OfflineBanner onRetry={() => setOfflinePreview(false)} /> : null}
        fab={
          isDetailRoute ? null : (
            <button type="button" className="fab" onClick={() => setShowQuickAdd(true)} aria-label="افزودن سریع">
              <Icon name="plus" aria-hidden="true" />
            </button>
          )
        }
      >
        {content}
      </AppShell>
      <GlobalSearch open={showSearch} onClose={() => setShowSearch(false)} onNavigate={navigate} />
      <QuickAddSheet
        open={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onSelect={handleQuickAddSelect}
        onBookAdd={handleQuickAddBook}
        onMediaAdd={handleQuickAddMedia}
        onFinanceAdd={handleQuickAddFinance}
      />
    </>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AppInner />
    </ToastProvider>
  );
}
